/**
 * ChatMessage Component
 * Renders a single chat message with bubble design (ChatGPT style)
 * Tools collapse into accordion when text response starts streaming
 */

import React, { useState } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { AgentBadge } from "./AgentBadge";
import { ToolExecutionView } from "./ToolExecutionView";
import { FileCard } from "./FileCard";
import { useToast } from "@/hooks/use-toast";
import type {
  AgentType,
  ChatMessage as ChatMessageType,
} from "@/types/chat.types";
import {
  User,
  Bot,
  Copy,
  Check,
  ChevronDown,
  ChevronRight,
  Brain,
  Loader2,
  FileX,
} from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Detects links that point to a generated/downloadable file. Downloads belong
 * in the FileCard button (rendered from the generate_file tool result), never
 * as inline markdown links: the presigned URL expires (10 min TTL) and any
 * model-emitted URL is unreliable. If the model slips one into its text, we
 * neutralize it here so the broken link never becomes clickable.
 */
const isFileDownloadUrl = (href?: string): boolean => {
  if (!href) return false;
  const url = href.toLowerCase();
  return (
    url.includes("digitaloceanspaces.com") ||
    url.includes("amazonaws.com") ||
    // trd-agent stable file endpoint
    (url.includes("/conversations/") && url.includes("/files/")) ||
    // presigned-URL signatures
    url.includes("x-amz-signature") ||
    url.includes("x-amz-credential") ||
    /[?&](token|expires)=/.test(url)
  );
};

interface ChatMessageProps {
  message: ChatMessageType;
  agentType?: AgentType;
  isLastMessage?: boolean;
  isStreaming?: boolean;
  compact?: boolean;
  conversationId?: string | null;
}

export const ChatMessage = ({
  message,
  agentType,
  isLastMessage = false,
  isStreaming = false,
  compact = false,
  conversationId,
}: ChatMessageProps) => {
  const isUser = message.role === "user";
  const timestamp = message.createdAt
    ? new Date(message.createdAt)
    : new Date();

  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  // Get text content for copy - AI SDK v6 uses message.parts
  const getTextContent = (): string => {
    if (!message.parts || message.parts.length === 0) return "";
    return message.parts
      .filter((part: any) => part.type === "text")
      .map((part: any) => part.text)
      .join("\n");
  };

  // Check if message has any text content
  const hasTextContent = message.parts?.some(
    (part: any) => part.type === "text" && part.text
  );

  // Check if message has dynamic tools (AI SDK v6 beta format)
  const hasDynamicTools = message.parts?.some(
    (part: any) =>
      part.type === "dynamic-tool" || part.type === "tool-code_execution"
  );

  // Get tool parts for rendering (excludes generate_file — rendered separately as file cards)
  const toolParts =
    message.parts?.filter((part: any) => {
      if (part.type === "tool-generate_file") return false;
      if (
        part.type === "dynamic-tool" ||
        part.type === "tool-call" ||
        part.type === "tool-result"
      ) {
        if (part.toolName === "generate_file") return false;
      }
      return (
        part.type === "dynamic-tool" ||
        part.type === "tool-code_execution" ||
        part.type === "tool-call"
      );
    }) || [];

  // Build file entries for generate_file, merging tool-call + tool-result pairs.
  // Backend emits them as separate parts linked by toolCallId (persisted shape).
  // Streaming (AI SDK v6) uses tool-generate_file or dynamic-tool with state + output.
  type FileEntry = {
    key: string;
    state?: string;
    output?: any;
    errorText?: string;
  };
  const fileEntries: FileEntry[] = [];
  const seenCallIds = new Set<string>();

  (message.parts || []).forEach((part: any, idx: number) => {
    const isStreamingFileTool =
      part.type === "tool-generate_file" ||
      ((part.type === "dynamic-tool" || part.type === "tool-call") &&
        part.toolName === "generate_file");

    if (isStreamingFileTool) {
      const callId = part.toolCallId;
      if (callId && seenCallIds.has(callId)) return;
      if (callId) seenCallIds.add(callId);

      // Look for a matching tool-result sibling (persisted shape)
      const resultPart = (message.parts || []).find(
        (p: any) =>
          p.type === "tool-result" &&
          p.toolName === "generate_file" &&
          callId &&
          p.toolCallId === callId
      ) as any;

      fileEntries.push({
        key: callId || `${part.type}-${idx}`,
        state: part.state,
        output: part.output || part.result || resultPart?.output,
        errorText: part.errorText || resultPart?.errorText,
      });
      return;
    }

    // Standalone tool-result for generate_file (no paired tool-call in parts)
    if (
      part.type === "tool-result" &&
      part.toolName === "generate_file" &&
      (!part.toolCallId || !seenCallIds.has(part.toolCallId))
    ) {
      if (part.toolCallId) seenCallIds.add(part.toolCallId);
      fileEntries.push({
        key: part.toolCallId || `tool-result-${idx}`,
        output: part.output,
      });
    }
  });

  // Auto-collapse tools when text appears
  // Only collapse if we have both tools and text
  const shouldAutoCollapse = hasDynamicTools && hasTextContent;

  // Start expanded while thinking, then collapse when text appears
  const [toolsExpanded, setToolsExpanded] = useState(true);

  // Auto-collapse when text appears (useEffect to update after first render)
  React.useEffect(() => {
    if (shouldAutoCollapse) {
      setToolsExpanded(false);
    }
  }, [shouldAutoCollapse]);

  const handleCopy = async () => {
    const text = getTextContent();
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast({
        title: "Copiado",
        description: "Mensaje copiado al portapapeles",
        variant: "success",
        duration: 2000,
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title: "Error",
        description: "No se pudo copiar el mensaje",
        variant: "error",
        duration: 3000,
      });
    }
  };

  return (
    <div
      className={cn(
        "group flex w-full",
        compact ? "gap-2 py-2 px-2" : "gap-4 py-4 px-6",
        isUser ? "justify-end" : "justify-start"
      )}
    >
      {/* Avatar - Only for assistant on left */}
      {!isUser && (
        <div className="flex-shrink-0">
          <Avatar className={cn(compact ? "h-6 w-6" : "h-8 w-8")}>
            <AvatarFallback className="bg-gradient text-white">
              <Bot className={cn(compact ? "h-3 w-3" : "h-4 w-4")} />
            </AvatarFallback>
          </Avatar>
        </div>
      )}
      {/* Message Bubble */}
      <div
        className={cn(
          "flex flex-col",
          compact ? "max-w-[85%]" : "max-w-[75%]",
          isUser ? "items-end" : "items-start"
        )}
      >
        {/* Header - Name, badge, and timestamp */}
        <div
          className={cn(
            "flex items-center gap-2 px-1",
            compact ? "mb-0.5" : "mb-1"
          )}
        >
          {!isUser && (
            <>
              <span
                className={cn("font-semibold", compact ? "text-xs" : "text-sm")}
              >
                Trud Agentic
              </span>
              {/* Hide badge in compact mode */}
              {agentType && !compact && <AgentBadge type={agentType} />}
            </>
          )}
          <span
            className={cn(
              "text-text-secondary",
              compact ? "text-[10px]" : "text-xs"
            )}
          >
            {format(timestamp, "HH:mm", { locale: es })}
          </span>
        </div>

        {/* Tool Executions - Collapsible chain of thought (BEFORE text) */}
        {hasDynamicTools && toolParts.length > 0 && (
          <div
            className={cn(
              compact ? "mt-1 mb-1" : "mt-3 mb-2",
              hasTextContent && (compact ? "mb-2" : "mb-3")
            )}
          >
            {/* Accordion Header - Always show */}
            <button
              type="button"
              onClick={() => setToolsExpanded(!toolsExpanded)}
              className={cn(
                "flex items-center gap-2 text-text-secondary hover:text-foreground transition-colors w-full rounded hover:bg-background-100/50",
                compact ? "text-[10px] py-0.5 px-1" : "text-xs py-1 px-2",
                "group"
              )}
            >
              {toolsExpanded ? (
                <ChevronDown
                  className={cn(compact ? "h-3 w-3" : "h-3.5 w-3.5")}
                />
              ) : (
                <ChevronRight
                  className={cn(compact ? "h-3 w-3" : "h-3.5 w-3.5")}
                />
              )}
              <Brain className={cn(compact ? "h-3 w-3" : "h-3.5 w-3.5")} />
              <span className="font-medium">
                {!hasTextContent && isStreaming && isLastMessage ? (
                  // Still thinking - show spinner
                  <>
                    Pensando
                    <Loader2 className="inline h-3 w-3 animate-spin ml-1" />
                  </>
                ) : (
                  // Done - show completed
                  <>
                    {toolsExpanded ? "Ocultar" : "Ver"}proceso de análisis (
                    {toolParts.length}pasos)
                  </>
                )}
              </span>
            </button>

            {/* Tools Chain - show when expanded */}
            {toolsExpanded && (
              <div
                className={cn(
                  "mt-2 space-y-0 transition-all duration-300",
                  "bg-background-50/30 rounded-lg py-2 px-3"
                )}
              >
                {(() => {
                  // Find all text parts to detect intermediate thoughts
                  const textParts =
                    message.parts?.filter((p: any) => p.type === "text") || [];

                  // The last text part is the final response
                  // const finalText = textParts[textParts.length - 1];

                  // Any text before the last one is intermediate thinking
                  const intermediateTexts = textParts.slice(0, -1);

                  return toolParts.map((part: any, idx) => {
                    // Find if there's an intermediate thought after this tool
                    // (this is a heuristic - text parts appear in order in the parts array)
                    const partIndexInAll = message.parts?.indexOf(part) || 0;
                    const nextTextPart = intermediateTexts.find(
                      (textPart: any) => {
                        const textIndex = message.parts?.indexOf(textPart) || 0;
                        return textIndex > partIndexInAll;
                      }
                    );

                    const intermediateThought =
                      nextTextPart?.type === "text"
                        ? (nextTextPart as any).text
                        : undefined;

                    // AI SDK v6 beta uses "dynamic-tool" instead of "tool-call"
                    if (
                      part.type === "dynamic-tool" ||
                      part.type === "tool-code_execution"
                    ) {
                      const isExecuting =
                        part.state === "streaming" ||
                        part.state === "executing";
                      const isLast = idx === toolParts.length - 1;

                      return (
                        <ToolExecutionView
                          key={idx}
                          toolName={part.toolName || part.type || "unknown"}
                          isExecuting={isExecuting}
                          isLast={isLast}
                          intermediateThought={intermediateThought}
                          index={idx}
                          totalTools={toolParts.length}
                          isStreamActive={isStreaming && isLastMessage}
                        />
                      );
                    }

                    // Legacy format support (tool-call/tool-result)
                    if (part.type === "tool-call") {
                      const resultPart = message.parts?.find(
                        (p: any) =>
                          p.type === "tool-result" &&
                          p.toolResultId === part.toolCallId
                      ) as any;
                      const isLast = idx === toolParts.length - 1;

                      return (
                        <ToolExecutionView
                          key={idx}
                          toolName={part.toolName || "unknown"}
                          isExecuting={!resultPart}
                          isLast={isLast}
                          intermediateThought={intermediateThought}
                          index={idx}
                          totalTools={toolParts.length}
                          isStreamActive={isStreaming && isLastMessage}
                        />
                      );
                    }

                    return null;
                  });
                })()}
              </div>
            )}
          </div>
        )}

        {/* Bubble Content - Show AFTER tools (below chain of thought) */}
        {hasTextContent && (
          <div
            className={cn(
              "shadow-sm",
              compact ? "rounded-2xl px-3 py-2" : "rounded-3xl px-5 py-3",
              isUser
                ? "bg-gradient text-white rounded-tr-sm"
                : "bg-background-100 text-foreground rounded-tl-sm border border-border"
            )}
          >
            {/* Message Content - AI SDK v6 uses message.parts */}
            <div className="text-sm leading-relaxed">
              {(() => {
                // Only show the LAST text part (final response) in the bubble
                // Intermediate thoughts are shown next to tools
                const textParts =
                  message.parts?.filter((part) => part.type === "text") || [];
                const finalText = textParts[textParts.length - 1];

                if (!finalText) return null;

                return (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      // Paragraph spacing
                      p: ({ children }) => (
                        <p
                          className={cn(
                            compact ? "mb-2 last:mb-0" : "mb-3 last:mb-0"
                          )}
                        >
                          {children}
                        </p>
                      ),
                      // Bold text
                      strong: ({ children }) => (
                        <strong className="font-semibold">{children}</strong>
                      ),
                      // Italic text
                      em: ({ children }) => (
                        <em className="italic">{children}</em>
                      ),
                      // Unordered lists
                      ul: ({ children }) => (
                        <ul className="list-disc pl-5 mb-3 space-y-1">
                          {children}
                        </ul>
                      ),
                      // Ordered lists
                      ol: ({ children }) => (
                        <ol className="list-decimal pl-5 mb-3 space-y-1">
                          {children}
                        </ol>
                      ),
                      // List items
                      li: ({ children }) => <li>{children}</li>,
                      // Inline code
                      code: ({ children }) => (
                        <code className="bg-background-200/50 px-1.5 py-0.5 rounded text-xs font-mono">
                          {children}
                        </code>
                      ),
                      // Code blocks
                      pre: ({ children }) => (
                        <pre className="bg-background-200/50 p-3 rounded overflow-x-auto mb-3 text-xs">
                          {children}
                        </pre>
                      ),
                      // Headings
                      h1: ({ children }) => (
                        <h1 className="text-lg font-bold mb-2 mt-4 first:mt-0">
                          {children}
                        </h1>
                      ),
                      h2: ({ children }) => (
                        <h2 className="text-base font-bold mb-2 mt-3 first:mt-0">
                          {children}
                        </h2>
                      ),
                      h3: ({ children }) => (
                        <h3 className="text-sm font-bold mb-2 mt-3 first:mt-0">
                          {children}
                        </h3>
                      ),
                      // Blockquotes
                      blockquote: ({ children }) => (
                        <blockquote className="border-l-4 border-border pl-4 italic mb-3">
                          {children}
                        </blockquote>
                      ),
                      // Horizontal rules
                      hr: () => <hr className="my-4 border-border" />,
                      // Links
                      a: ({ children, href }) => {
                        // File downloads live in the FileCard button below the
                        // bubble, not as inline links. Render any file-download
                        // link as plain text so a broken/expired URL is never
                        // clickable.
                        if (isFileDownloadUrl(href)) {
                          return <span>{children}</span>;
                        }
                        return (
                          <a
                            href={href}
                            className="text-blue-500 hover:underline"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {children}
                          </a>
                        );
                      },
                      // Tables
                      table: ({ children }) => (
                        <div
                          className={cn(
                            "overflow-x-auto",
                            compact ? "my-2" : "my-4"
                          )}
                        >
                          <table className="min-w-full border-collapse border border-border text-sm">
                            {children}
                          </table>
                        </div>
                      ),
                      thead: ({ children }) => (
                        <thead className="bg-background-200/50">
                          {children}
                        </thead>
                      ),
                      tbody: ({ children }) => <tbody>{children}</tbody>,
                      tr: ({ children }) => (
                        <tr className="border-b border-border hover:bg-background-100/50 transition">
                          {children}
                        </tr>
                      ),
                      th: ({ children }) => (
                        <th
                          className={cn(
                            "text-left font-semibold border border-border",
                            compact ? "px-2 py-1" : "px-4 py-2"
                          )}
                        >
                          {children}
                        </th>
                      ),
                      td: ({ children }) => (
                        <td
                          className={cn(
                            "border border-border",
                            compact ? "px-2 py-1" : "px-4 py-2"
                          )}
                        >
                          {children}
                        </td>
                      ),
                    }}
                  >
                    {finalText.text}
                  </ReactMarkdown>
                );
              })()}
            </div>
          </div>
        )}

        {/* Generated files - rendered as separate cards below the bubble */}
        {fileEntries.length > 0 && (
          <div className={cn("flex flex-col gap-2", compact ? "mt-2" : "mt-3")}>
            {fileEntries.map((entry) => {
              // generate_file can fail either by throwing (output-error /
              // errorText) or by returning { ok: false, error } — both must
              // surface the error card, not hang on the "generando" spinner.
              const failedOutput = entry.output && entry.output.ok === false;
              if (
                entry.state === "output-error" ||
                entry.errorText ||
                failedOutput
              ) {
                return (
                  <div
                    key={entry.key}
                    className={cn(
                      "flex items-center gap-2 border border-error-border bg-error-light text-error-foreground rounded-xl",
                      compact
                        ? "px-3 py-2 text-xs max-w-xs"
                        : "px-4 py-3 text-sm max-w-sm"
                    )}
                  >
                    <FileX
                      className={cn(
                        "flex-shrink-0",
                        compact ? "h-4 w-4" : "h-5 w-5"
                      )}
                    />
                    <span>No se pudo generar el archivo</span>
                  </div>
                );
              }

              if (entry.output && entry.output.fileId) {
                return (
                  <FileCard
                    key={entry.key}
                    conversationId={conversationId || ""}
                    fileId={entry.output.fileId}
                    filename={entry.output.filename || "archivo"}
                    sizeBytes={entry.output.sizeBytes}
                    mime={entry.output.mime}
                    compact={compact}
                    downloadUrl={entry.output.downloadUrl}
                  />
                );
              }

              // Still running (input-streaming, input-available, executing, etc.)
              return (
                <div
                  key={entry.key}
                  className={cn(
                    "flex items-center gap-2 border border-border bg-background-100 text-text-secondary rounded-xl",
                    compact
                      ? "px-3 py-2 text-xs max-w-xs"
                      : "px-4 py-3 text-sm max-w-sm"
                  )}
                >
                  <Loader2
                    className={cn(
                      "animate-spin flex-shrink-0",
                      compact ? "h-4 w-4" : "h-5 w-5"
                    )}
                  />
                  <span>Generando archivo…</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty state - only if truly no content */}
        {!hasTextContent && (!message.parts || message.parts.length === 0) && (
          <div
            className={cn(
              "rounded-3xl px-5 py-3 shadow-sm",
              "bg-background-100 text-foreground rounded-tl-sm border border-border"
            )}
          >
            <div className="text-text-secondary italic text-sm">
              Pensando...
            </div>
          </div>
        )}

        {/* Actions - Below bubble (AI SDK style) - Hide in compact mode */}
        {!isUser && !compact && (
          <div className="flex items-center gap-1 px-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={handleCopy}
            >
              {copied ? (
                <>
                  <Check className="h-3 w-3 mr-1 text-green-500" />
                  Copiado
                </>
              ) : (
                <>
                  <Copy className="h-3 w-3 mr-1" />
                  Copiar
                </>
              )}
            </Button>
          </div>
        )}
      </div>
      {/* Avatar - User on right */}
      {isUser && (
        <div className="flex-shrink-0">
          <Avatar className={cn(compact ? "h-6 w-6" : "h-8 w-8")}>
            <AvatarFallback className="bg-gradient text-white">
              <User className={cn(compact ? "h-3 w-3" : "h-4 w-4")} />
            </AvatarFallback>
          </Avatar>
        </div>
      )}
    </div>
  );
};
